import { IFormOperationAnswerField, ITextLocaleMap } from '../models/operations.models';
import {
	transformDate,
	NOT_AVAILABLE_DISPLAY,
	isNotAvailableDisplay,
	isPermanentExpirationDate,
	PERMANENT_EXPIRATION_I18N_KEY,
	PERMANENT_EXPIRATION_DISPLAY,
} from '../../core/utils/utils';
import { resolveWorkflowLocaleText } from '../../workflows/utils/workflow-locale-text.util';

const CALENDAR_FIELD_IDS = new Set([
	'birth_date',
	'birthdate',
	'date',
	'expiration_date',
	'expiry_date',
	'expiry',
]);

const EXPIRATION_FIELD_IDS = new Set(['expiration_date', 'expiry_date', 'expiry']);
const HIDDEN_WHEN_EMPTY_IDS = new Set(['phone_prefix']);
const ISO_DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/;

export type FormOperationDisplayDeps = {
	translate: (key: string) => string;
	hasTranslation: (key: string) => boolean;
	/** Idioma activo del panel para resolver labels i18n de opciones. */
	locale?: string;
};

/** Tipos de campo cuyo `value` es una referencia a opciones (índice/valor), no texto libre. */
const OPTION_FIELD_TYPES = new Set(['select', 'radio', 'checkbox', 'single_select', 'multi_select']);

/** Presets workflow cuyo `id` implica opciones aunque `type` sea genérico. */
const OPTION_FIELD_IDS = new Set(['single_select', 'multi_select', 'checkbox', 'legal']);

/** Campos de selección única/múltiple mostrados como lista en modal actividad. */
const SELECT_LIST_FIELD_IDS = new Set(['single_select', 'multi_select']);
const SELECT_LIST_FIELD_TYPES = new Set(['single_select', 'multi_select']);

/** Presets checkbox/legal: la respuesta es aceptación (Sí/No), no el HTML de la opción. */
const BOOLEAN_CHECKBOX_FIELD_IDS = new Set(['checkbox', 'legal']);

/** Preset Sí/No: el valor es `yes`/`no`, no la etiqueta guardada en journey (suele venir en español). */
const YES_NO_FIELD_IDS = new Set(['yes_no']);

/** Campo de fecha/calendario por id, nombre o validador `date`. */
export function isFormOperationCalendarField(field: Pick<IFormOperationAnswerField, 'id' | 'name' | 'validators'>): boolean {
	const key = (field.id || field.name || '').trim().toLowerCase();
	if (CALENDAR_FIELD_IDS.has(key)) {
		return true;
	}
	return (field.validators ?? []).includes('date');
}

/** Campo con cotejo OCR activo (`validators` incluye `match_ocr`). */
export function isFormOperationMatchOcrField(field: Pick<IFormOperationAnswerField, 'validators'>): boolean {
	return (field.validators ?? []).includes('match_ocr');
}

/** Algún campo del paso tiene cotejo match_ocr en KO (NIF-553). */
export function hasFormFieldMatchOcrKo(
	fields: Pick<IFormOperationAnswerField, 'match_ocr_result'>[] | undefined,
): boolean {
	return (fields ?? []).some((field) => field.match_ocr_result?.status === 'KO');
}

export function hasFormOperationFieldValue(value: string | null | undefined): boolean {
	if (value == null) {
		return false;
	}
	return value.trim().length > 0;
}

/** Indica si el campo es obligatorio según `validators`. */
export function isFormOperationFieldRequired(
	field: Pick<IFormOperationAnswerField, 'validators'>,
): boolean {
	return (field.validators ?? []).includes('required');
}

/** Omite prefijo telefónico vacío; oculta opcionales sin valor (Figma/NIF-552). */
export function shouldIncludeFormOperationDetailField(
	field: IFormOperationAnswerField,
	configValidators?: string[] | null,
): boolean {
	const key = (field.id || field.name || '').trim().toLowerCase();
	if (HIDDEN_WHEN_EMPTY_IDS.has(key) && !hasFormOperationFieldValue(field.value)) {
		return false;
	}
	if (hasFormOperationFieldValue(field.value)) {
		return true;
	}
	const validators = configValidators ?? field.validators ?? [];
	if (!isFormOperationFieldRequired({ validators })) {
		return false;
	}
	return true;
}

/** Valor listo para `tec-info-group`: fechas dd/MM/yyyy, país y tipo documento traducidos. */
export function formatFormOperationFieldDisplayValue(
	field: IFormOperationAnswerField,
	deps: FormOperationDisplayDeps,
): string {
	const raw = field.value?.trim() ?? '';
	if (!raw) {
		return NOT_AVAILABLE_DISPLAY;
	}

	const key = (field.id || field.name || '').trim().toLowerCase();

	if (key === 'country') {
		return translateMasterKey(deps, `MASTER.COUNTRIES.${raw}`, raw);
	}

	if (key === 'id_document_type' || key === 'document_type') {
		const docKey = `MASTER.DOCUMENT_TYPE.${raw.toUpperCase()}`;
		const translated = translateMasterKey(deps, docKey, '');
		if (translated) {
			return translated;
		}
		if (raw === 'Pasaporte') {
			return translateMasterKey(deps, 'MASTER.DOCUMENT_TYPE.PASSPORT', raw);
		}
		return raw;
	}

	if (isFormOperationBooleanCheckboxField(field)) {
		return formatBooleanCheckboxDisplayValue(raw, deps);
	}

	if (isFormOperationYesNoField(field)) {
		return formatYesNoDisplayValue(raw, deps);
	}

	if (hasSelectableOptions(field)) {
		return formatOptionFieldDisplayValue(field, raw, deps);
	}

	if (isFormOperationCalendarField(field) || ISO_DATE_VALUE_PATTERN.test(raw)) {
		if (EXPIRATION_FIELD_IDS.has(key) && isPermanentExpirationDate(raw)) {
			return translateMasterKey(deps, PERMANENT_EXPIRATION_I18N_KEY, PERMANENT_EXPIRATION_DISPLAY);
		}
		return formatCalendarDisplayValue(raw);
	}

	return raw;
}

/** Preset workflow Sí/No (`radio` con valores `yes`/`no`). */
export function isFormOperationYesNoField(
	field: Pick<IFormOperationAnswerField, 'id' | 'name'>,
): boolean {
	const key = (field.id || field.name || '').trim().toLowerCase();
	return YES_NO_FIELD_IDS.has(key);
}

/** Checkbox/legal (rich text): el valor es aceptación, no la etiqueta HTML de la opción. */
export function isFormOperationBooleanCheckboxField(field: IFormOperationAnswerField): boolean {
	const key = (field.id || field.name || '').trim().toLowerCase();
	if (BOOLEAN_CHECKBOX_FIELD_IDS.has(key)) {
		return true;
	}
	if ((field.type ?? '').toLowerCase() !== 'checkbox') {
		return false;
	}
	return (field.options ?? []).some((option) => isRichTextOptionLabel(option.label));
}

function isRichTextOptionLabel(label: unknown): boolean {
	if (label == null) {
		return false;
	}
	if (typeof label === 'object' && 'html' in (label as object)) {
		return true;
	}
	if (typeof label === 'string') {
		return looksLikeHtml(label);
	}
	if (typeof label === 'object') {
		return Object.values(label as Record<string, unknown>).some(
			(value) => typeof value === 'string' && looksLikeHtml(value),
		);
	}
	return false;
}

function looksLikeHtml(value: string): boolean {
	return /<[a-z][\s\S]*>/i.test(value.trim());
}

function formatBooleanCheckboxDisplayValue(raw: string, deps: FormOperationDisplayDeps): string {
	if (isCheckboxUncheckedValue(raw)) {
		return translateMasterKey(deps, 'GENERAL.NO', 'No');
	}
	return translateMasterKey(deps, 'GENERAL.YES', 'Sí');
}

function formatYesNoDisplayValue(raw: string, deps: FormOperationDisplayDeps): string {
	const normalized = raw.trim().toLowerCase();
	if (normalized === 'yes' || normalized === 'true' || normalized === '1') {
		return translateMasterKey(deps, 'GENERAL.YES', 'Sí');
	}
	if (normalized === 'no' || normalized === 'false' || normalized === '0') {
		return translateMasterKey(deps, 'GENERAL.NO', 'No');
	}
	return raw;
}

function isCheckboxUncheckedValue(raw: string): boolean {
	const trimmed = raw.trim().toLowerCase();
	if (!trimmed || trimmed === 'false' || trimmed === '0' || trimmed === 'null' || trimmed === 'undefined') {
		return true;
	}
	if (trimmed === '[]') {
		return true;
	}
	return false;
}

/** Campo con catálogo de opciones (select/radio/checkbox) y `options[]` disponibles. */
function hasSelectableOptions(field: IFormOperationAnswerField): boolean {
	const type = (field.type ?? '').toLowerCase();
	const key = (field.id || field.name || '').trim().toLowerCase();
	if (OPTION_FIELD_TYPES.has(type) || OPTION_FIELD_IDS.has(key)) {
		return (field.options?.length ?? 0) > 0;
	}
	return false;
}

/**
 * Resuelve el/los `value` del campo a las etiquetas i18n de sus opciones.
 * `value` puede ser un valor simple (`"2"`) o un array JSON (`["1","5"]`) para selección múltiple.
 */
function formatOptionFieldDisplayValue(
	field: IFormOperationAnswerField,
	raw: string,
	deps: FormOperationDisplayDeps,
): string {
	const selectedValues = parseOptionValues(raw);
	if (!selectedValues.length) {
		return raw;
	}

	const labels = selectedValues.map((value) => resolveOptionLabel(field, value, deps));
	return labels.join(', ');
}

/** Normaliza el `value` a lista de valores string (array JSON o valor único). */
function parseOptionValues(raw: string): string[] {
	const trimmed = raw.trim();
	if (trimmed.startsWith('[')) {
		try {
			const parsed = JSON.parse(trimmed);
			if (Array.isArray(parsed)) {
				return parsed.map((item) => String(item).trim()).filter((item) => item.length > 0);
			}
		} catch {
			// Valor no es JSON válido; se trata como valor único.
		}
	}
	return [trimmed];
}

/** Devuelve la etiqueta localizada de la opción cuyo `value` coincide; si no hay, el valor crudo. */
function resolveOptionLabel(
	field: IFormOperationAnswerField,
	value: string,
	deps: FormOperationDisplayDeps,
): string {
	const option = (field.options ?? []).find((opt) => String(opt.value).trim() === value);
	if (!option) {
		return value;
	}
	const label = resolveWorkflowLocaleText(option.label as string | ITextLocaleMap, deps.locale ?? 'es');
	return label || value;
}

/** Selección única/múltiple FORM (preset `single_select` / `multi_select`). */
export function isFormOperationSelectListField(
	field: Pick<IFormOperationAnswerField, 'id' | 'name' | 'type'>,
): boolean {
	const key = (field.id || field.name || '').trim().toLowerCase();
	if (SELECT_LIST_FIELD_IDS.has(key)) {
		return true;
	}
	return SELECT_LIST_FIELD_TYPES.has((field.type ?? '').toLowerCase());
}

/**
 * Etiquetas de las opciones seleccionadas (una por ítem) para listas en actividad FORM.
 */
export function resolveFormOperationFieldOptionLabels(
	field: IFormOperationAnswerField,
	deps: FormOperationDisplayDeps,
): string[] {
	const raw = field.value?.trim() ?? '';
	if (!raw) {
		return [];
	}

	if (!isFormOperationSelectListField(field) || !hasSelectableOptions(field)) {
		const value = formatFormOperationFieldDisplayValue(field, deps);
		return value && !isNotAvailableDisplay(value) ? [value] : [];
	}

	return parseOptionValues(raw).map((value) => resolveOptionLabel(field, value, deps));
}

function formatCalendarDisplayValue(raw: string): string {
	const parsed = transformDate(raw) ?? parseSlashDate(raw);
	if (!parsed || Number.isNaN(parsed.getTime())) {
		return raw;
	}
	const day = String(parsed.getDate()).padStart(2, '0');
	const month = String(parsed.getMonth() + 1).padStart(2, '0');
	return `${day}/${month}/${parsed.getFullYear()}`;
}

function parseSlashDate(value: string): Date | undefined {
	const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
	if (!slashMatch) {
		return undefined;
	}
	const date = new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]));
	return Number.isNaN(date.getTime()) ? undefined : date;
}

function translateMasterKey(deps: FormOperationDisplayDeps, key: string, fallback: string): string {
	if (deps.hasTranslation(key)) {
		return deps.translate(key);
	}
	return fallback;
}
